import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FormSection from 'components/shared/FormSection/index';
import FormStatus from 'components/shared/FormCommon/FormStatus';
import { useLocation, useParams } from 'react-router-dom';
import PromotionInformation from './components/add/PromotionsInformation';
import PromotionsCondition from './components/add/PromotionsCondition';
import PromotionComboApply from './components/add/PromotionComboApply';
import CouponProduct from 'pages/Coupon/components/add/CouponProduct';
import CouponCustomerType from 'pages/Coupon/components/add/CouponCustomerType';
import PromotionProductCategory from './components/add/PromotionProductCategory';
import PromotionSetting from './components/add/PromotionSetting';
import PromotionsPaymentApply from './components/add/PromotionsPaymentApply';
import PromotionPOOffersApply from './components/add/PromotionPOOffersApply';
import PromotionNote from './components/add/PromotionNote';
import { create, getDetail, update } from 'services/promotions.service';
import { showToast } from 'utils/helpers';
// import CommissionReview from 'pages/Commission/components/Sections/CommissionReview';
import PromotionReview from 'pages/PromotionOffers/components/add/PromotionReview';
import { TYPE_REVIEW, VALUE_DEFAULT } from 'pages/Promotions/utils/constants';
import PromotionLifecycle from './components/add/PromotionLifecycle';

const collectValidationErrors = (errors, fieldPath = '') => {
  if (!errors || typeof errors !== 'object') return [];

  if (errors.message) {
    return [
      {
        field: fieldPath,
        message: errors.message,
      },
    ];
  }

  return Object.entries(errors).flatMap(([field, error]) =>
    collectValidationErrors(error, fieldPath ? `${fieldPath}.${field}` : field),
  );
};

const PromotionsAddPage = () => {
  const params = useParams();
  const { pathname } = useLocation();
  const [checkReview, setCheckReview] = useState(false);
  const disabled = React.useMemo(() => pathname.includes('/detail'), [pathname]);
  const methods = useForm({
    defaultValues: VALUE_DEFAULT,
  });
  const loadDetail = useCallback(() => {
    if(params?.promotion_id) {
      return getDetail(params?.promotion_id)
      .then((o) => {
        const isPromotionByTotalQuantity =
          o?.is_promotion_by_total_quantity ?? o?.is_promorion_by_total_quantity ?? 0;
        methods.reset({
          ...o,
          is_promotion_by_total_quantity: isPromotionByTotalQuantity,
          is_promorion_by_total_quantity: isPromotionByTotalQuantity,
          apply_birthday_list: o?.apply_birthday_list
            ? (o?.apply_birthday_list).split(',').map((o) => parseInt(o))
            : [],
        });
        setCheckReview(Number(o?.is_review) !== TYPE_REVIEW.PENDING);
      })
      .catch((error) => {
        showToast.error(error?.message || 'Không tải được chương trình khuyến mại.');
      });
    }
  }, [params.promotion_id, methods]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const detailForm = [
    { id: 'promotion_lifecycle', title: 'Hiệu lực chương trình', component: PromotionLifecycle, loadDetail },
    {
      id: 'information_detail',
      title: 'Thông tin chương trình khuyến mại',
      component: PromotionInformation,
      fieldActive: ['promotion_name', 'begin_date', 'end_date'],
    },
    {
      id: 'condition_detail',
      title: 'Điều kiện khuyến mại',
      component: PromotionsCondition,
    },
    {
      id: 'combo_apply',
      title: 'Combo áp dụng khuyến mại',
      component: PromotionComboApply,
      hidden: !methods.watch('is_combo_promotion'),
    },
    {
      id: 'product_apply',
      title: 'Danh sách sản phẩm áp dụng khuyến mại',
      component: CouponProduct,
    },
    {
      id: 'information_detail',
      title: 'Ngành hàng áp dụng',
      component: PromotionProductCategory,
    },
    {
      id: 'information_payment',
      title: 'Danh sách ưu đãi khuyến mại',
      component: PromotionPOOffersApply,
    },
    {
      id: 'information_detail',
      title: 'Hạng khách hàng được hưởng khuyến mại',
      component: CouponCustomerType,
    },
    {
      id: 'information_payment',
      title: 'Danh sách hình thức thanh toán được áp dụng',
      component: PromotionsPaymentApply,
    },
    {
      id: 'form_status_review',
      title: 'Mức duyệt',
      component: PromotionReview,
      loadDetail: loadDetail,
    },
    {
      id: 'form_status',
      title: 'Thiết lập khác',
      component: PromotionSetting,
    },
    {
      id: 'form_note',
      title: 'Ghi chú',
      component: PromotionNote,
    },
    {
      id: 'form_status',
      title: 'Trạng thái',
      component: FormStatus,
      fieldActive: ['is_active'],
    },
  ];

  const onSubmit = async (payload) => {
    try {
      if (!payload?.is_all_payment_form && !payload?.payment_form_list?.length) {
        showToast.warning('Hình thức thanh toán áp dụng là bắt buộc', {
          fields: ['check_payment_form'],
          toastId: 'promotion-payment-form-required',
        });
        return;
      }

      let label;
      const isPromotionByTotalQuantity =
        payload?.is_promotion_by_total_quantity ?? payload?.is_promorion_by_total_quantity ?? 0;
      payload.is_promotion_by_total_quantity = isPromotionByTotalQuantity ? 1 : 0;
      payload.is_promorion_by_total_quantity = isPromotionByTotalQuantity ? 1 : 0;
      payload.is_combo_promotion = payload?.is_combo_promotion ? 1 : 0;
      payload.combo_apply_list = Array.isArray(payload?.combo_apply_list) ? payload.combo_apply_list : [];
      if (payload.is_combo_promotion && payload.combo_apply_list.length > 0) {
        payload.is_all_product = 0;
      }
      payload.apply_birthday_list = (payload.apply_birthday_list ?? []).map((o) => o?.value ?? o)?.toString();
      payload.order_type_list = (payload?.order_type_list ?? [])?.map((e) => e?.id ?? e);

      if (params?.promotion_id) {
        await update(params?.promotion_id, payload);
        label = 'Chỉnh sửa';
      } else {
        await create(payload);
        label = 'Thêm mới';
        methods.reset(VALUE_DEFAULT);
      }
      showToast.success(`${label} thành công`);
    } catch (error) {
      showToast.error(error?.message ?? 'Có lỗi xảy ra');
    }
  };

  const onInvalid = useCallback((errors) => {
    const validationErrors = collectValidationErrors(errors);
    const firstError = validationErrors[0];
    showToast.warning(firstError?.message || 'Vui lòng nhập đầy đủ thông tin bắt buộc', {
      fields: validationErrors.map((error) => error.field).filter(Boolean),
      toastId: 'promotion-validation-error',
    });
  }, []);

  const actions = [
    {
      globalAction: true,
      icon: 'fi fi-rr-check',
      type: 'success',
      submit: true,
      content: params?.promotion_id ? 'Hoàn tất chỉnh sửa' : 'Hoàn tất thêm mới',
      hidden:
        disabled ||
        methods.watch('is_review') == TYPE_REVIEW.ACCPECT ||
        methods.watch('is_review') == TYPE_REVIEW.REJECT,
    },
  ];

  return (
    <FormProvider {...methods}>
      <FormSection
        disabled={disabled || checkReview}
        detailForm={detailForm}
        onSubmit={onSubmit}
        onInvalid={onInvalid}
        actions={actions}
      />
    </FormProvider>
  );
};

export default PromotionsAddPage;
